import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Paper, Typography, Link } from '@mui/material';
import { Order } from 'models/order';
import RestorationTypes from 'components/orders/shared/restoration-types.component';
import DeliveryDetails from 'components/orders/shared/delivery-details.component';
import OrderItemIconArea from 'components/orders/shared/order-item-icon-area/order-item-icon-area.component';
import LabFlagButton from 'components/shared/lab-flag-button/lab-flag-button.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { NavLink } from 'react-router-dom';
import { isSupplier } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';

type StatusItemProps = {
    order: Order;
};
const StatusItem: FC<StatusItemProps> = ({ order }: StatusItemProps) => {
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const { t: translate } = useTranslation(['orders', 'common']);

    const orderDetailsUrl = (order: Order) => {
        return `/order-details/${order._id}/main-view`;
    };

    return (
        <Fragment key={order._id}>
            <Box mt={0.25}>
                <Link component={NavLink} to={orderDetailsUrl(order)} color="inherit" underline="none">
                    <Paper square sx={{ cursor: 'pointer' }}>
                        <Box px={1} py={1}>
                            <Typography variant="body2" component="div">
                                <Box>
                                    <strong>{translate('common:PATIENT')}:</strong>{' '}
                                    <span>
                                        {formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}
                                    </span>
                                </Box>

                                <Box display="flex">
                                    <strong>{translate('orders:CONSTRUCTIONS')}:</strong>
                                    &nbsp;
                                    <RestorationTypes
                                        sampleType={order.deliveryDetails.sampleType}
                                        types={order.restorationTypes}
                                        showIcon={true}
                                    />
                                </Box>
                                <Box>
                                    <strong>{translate('common:DENTIST')}:</strong>{' '}
                                    <span>{order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}</span>
                                </Box>

                                {isSupplier(currentOrganization._id, order.supplier._id) ? (
                                    <Box mb={0.5}>
                                        <strong>{translate('common:CUSTOMER')}:</strong>{' '}
                                        <span>{order.customer?.name}</span>
                                    </Box>
                                ) : (
                                    <Box mb={0.5}>
                                        <strong>{translate('common:LAB')}:</strong> <span>{order.supplier.name}</span>
                                    </Box>
                                )}

                                <Grid container>
                                    <Grid item xl={7} lg={12}>
                                        <Box display="flex">
                                            <Box mr={0.5}>{translate('orders:DELIVERY')}:</Box>
                                            <DeliveryDetails details={order.deliveryDetails} />
                                        </Box>
                                    </Grid>
                                    <Grid item xl={5} lg={12} justifyContent="flex-end" display="flex">
                                        {isSupplier(currentOrganization._id, order.supplier._id) ? (
                                            <LabFlagButton order={order} />
                                        ) : undefined}
                                        <OrderItemIconArea order={order} />
                                    </Grid>
                                </Grid>
                            </Typography>
                        </Box>
                    </Paper>
                </Link>
            </Box>
        </Fragment>
    );
};

export default StatusItem;
